import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Calculator, Calendar, Sparkles, FileDown, Video,
  Home, TrendingUp, Wallet, ShieldCheck, Loader2, RefreshCw,
  Check, Ruler, Coins, X, ChevronRight,
} from 'lucide-react';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import BookSeniorModal from './BookSeniorModal';
import { APARTMENT_TYPES, APARTMENT_TYPES_CHUMPHON } from '../lib/financial';

/**
 * ★ 2026-08-14 (Fase 51.2) — PaymentSimulator (wizard mode)
 *
 * Nuovo flusso guidato in 5 step su TUTTE le card:
 *   1) Budget           — grandi preset €
 *   2) Metri quadri     — preset mq
 *   3) Immobile         — proposto dalla disponibilità reale
 *   4) Anticipo         — preset % (10/25/50/100)
 *   5) Piano rate live  — schedule ricalcolato da oggi
 *
 * Fallback tipologie brochure per Chumphon/Spille (nessuna unità DB).
 * ★ 2026-08-26 (Fase 55) — Multi-language (react-i18next).
 */

// ── Helpers ─────────────────────────────────────────────────────────────
const fmtEUR = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(Number(v));
};

const fmtDateI18n = (iso, locale = 'it-IT') => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

// Preset Budget (min/max €) — labels via i18n
const BUDGET_PRESETS = [
  { key: 'all',      shortKey: 'resortDetail.sim.budget.any',      min: 0,       max: Infinity },
  { key: 'lt100k',   shortKey: 'resortDetail.sim.budget.lt100k',   min: 0,       max: 100000 },
  { key: '100to200', shortKey: 'resortDetail.sim.budget.100to200', min: 100000,  max: 200000 },
  { key: '200to350', shortKey: 'resortDetail.sim.budget.200to350', min: 200000,  max: 350000 },
  { key: '350to600', shortKey: 'resortDetail.sim.budget.350to600', min: 350000,  max: 600000 },
  { key: 'gt600k',   shortKey: 'resortDetail.sim.budget.gt600k',   min: 600000,  max: Infinity },
];

// Preset Mq (min/max mq)
const SQM_PRESETS = [
  { key: 'all',     shortKey: 'resortDetail.sim.sqm.any',      min: 0,   max: Infinity },
  { key: 'lt70',    shortKey: 'resortDetail.sim.sqm.lt70',     min: 0,   max: 70 },
  { key: '70to100', shortKey: 'resortDetail.sim.sqm.70to100',  min: 70,  max: 100 },
  { key: '100to150',shortKey: 'resortDetail.sim.sqm.100to150', min: 100, max: 150 },
  { key: 'gt150',   shortKey: 'resortDetail.sim.sqm.gt150',    min: 150, max: Infinity },
];

// Fallback tipologie per Chumphon/Spille
const BROCHURE_TYPES = {
  chumphon: APARTMENT_TYPES_CHUMPHON,
  spille:   APARTMENT_TYPES,
};

// Mappa locale i18n → BCP47
const LOCALE_MAP = { it: 'it-IT', en: 'en-GB', es: 'es-ES' };

export default function PaymentSimulator({ slug, grandOpening, projectName }) {
  const { t, i18n } = useTranslation();
  const dateLocale = LOCALE_MAP[i18n.language] || 'it-IT';
  const [dbUnits, setDbUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  // Step values
  const [budgetKey, setBudgetKey] = useState('all');
  const [sqmKey, setSqmKey]       = useState('all');
  const [selectedId, setSelectedId] = useState('');

  const [quote, setQuote] = useState(null);
  const [computing, setComputing] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── Load DB units ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    setLoadingUnits(true);
    api.get(`/public/projects/${slug}/units`)
      .then((r) => setDbUnits(Array.isArray(r.data?.units) ? r.data.units : []))
      .catch(() => setDbUnits([]))
      .finally(() => setLoadingUnits(false));
  }, [slug]);

  const brochureTypes = BROCHURE_TYPES[slug] || null;
  const hasDbUnits = dbUnits.length > 0;

  // Universal "properties" list (unified schema for filter/display)
  const properties = useMemo(() => {
    if (hasDbUnits) {
      return dbUnits
        .filter((u) => u.price_total)
        .map((u) => ({
          id: u.id || u.code,
          code: u.code,
          label: `${t('resortDetail.sim.property.unitLabel', 'Unità {{code}}', { code: u.code })}${u.block ? ` · ${u.block}` : ''}`,
          mq: Number(u.mq || 0),
          price: Number(u.price_total || 0),
          isAvailable: u.status === 'available',
          statusLabel: u.status === 'available'
            ? t('resortDetail.sim.property.availableTag', 'Disponibile')
            : t('resortDetail.sim.property.unavailableTag', 'Non disponibile'),
        }));
    }
    if (brochureTypes) {
      return brochureTypes.map((tp) => ({
        id: tp.key,
        code: tp.name,
        label: tp.name,
        mq: Number(tp.sqm || 0),
        price: Number(tp.price || 0),
        isAvailable: true,
        statusLabel: t('resortDetail.sim.property.brochureTag', 'Tipologia brochure'),
      }));
    }
    return [];
  }, [dbUnits, hasDbUnits, brochureTypes, t]);

  // Applica filtri Budget + Mq
  const budget = BUDGET_PRESETS.find((b) => b.key === budgetKey) || BUDGET_PRESETS[0];
  const sqm = SQM_PRESETS.find((s) => s.key === sqmKey) || SQM_PRESETS[0];

  const filteredProperties = useMemo(() => {
    return properties.filter((p) => {
      if (p.price < budget.min || p.price > budget.max) return false;
      if (p.mq < sqm.min || p.mq > sqm.max) return false;
      return true;
    });
  }, [properties, budget, sqm]);

  const availableFiltered = useMemo(
    () => filteredProperties.filter((p) => p.isAvailable),
    [filteredProperties],
  );

  // Auto-select prima disponibile quando cambiano i filtri
  useEffect(() => {
    if (filteredProperties.length === 0) {
      setSelectedId('');
      return;
    }
    // Preferisci disponibile → poi non-disponibile
    const target = availableFiltered[0] || filteredProperties[0];
    if (!filteredProperties.find((p) => p.id === selectedId)) {
      setSelectedId(target.id);
    }
  }, [filteredProperties, availableFiltered, selectedId]);

  // ── Compute quote (server) ─────────────────────────────────────────────
  const computeQuote = useCallback(async () => {
    if (!selectedId) { setQuote(null); return; }
    const sel = filteredProperties.find((p) => p.id === selectedId);
    if (!sel) { setQuote(null); return; }
    setComputing(true);
    try {
      const payload = hasDbUnits
        ? { project_slug: slug, unit_id: selectedId, lang: i18n.language }
        : { project_slug: slug, price_total: sel.price, label: `${sel.label} · ${sel.mq} mq`, lang: i18n.language };
      const { data } = await api.post(`/public/projects/${slug}/simulator-quote`, payload);
      setQuote(data);
    } catch {
      setQuote(null);
    } finally {
      setComputing(false);
    }
  }, [slug, selectedId, hasDbUnits, filteredProperties]);

  useEffect(() => { computeQuote(); }, [computeQuote]);

  const currentProp = filteredProperties.find((p) => p.id === selectedId) || null;

  // ── Download PDF ───────────────────────────────────────────────────────
  const downloadPdf = async () => {
    if (!currentProp) {
      toast.error(t('resortDetail.sim.toast.selectFirst', 'Seleziona prima un immobile'));
      return;
    }
    setPdfLoading(true);
    try {
      const payload = hasDbUnits
        ? { project_slug: slug, unit_id: selectedId, lang: i18n.language }
        : { project_slug: slug, price_total: currentProp.price, label: `${currentProp.label} · ${currentProp.mq} mq`, lang: i18n.language };
      const res = await api.post(
        `/public/projects/${slug}/simulator-quote.pdf`,
        payload,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivo_${slug}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('resortDetail.sim.toast.pdfDownloaded', 'Preventivo PDF scaricato'));
    } catch {
      toast.error(t('resortDetail.sim.toast.pdfError', 'Errore generazione PDF'));
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <section
      className="rounded-3xl border-2 border-[var(--gp-gold)]/30 bg-gradient-to-br from-[var(--gp-gold)]/5 via-white to-[var(--gp-forest)]/5 p-4 md:p-6 lg:p-8 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
      data-testid="payment-simulator"
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5 md:mb-6">
        <div>
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[var(--gp-gold)] font-semibold inline-flex items-center gap-1.5">
            <Calculator className="w-3.5 h-3.5" />
            {t('resortDetail.sim.header.eyebrow', 'Simulatore piano pagamenti · Live')}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1.5 leading-tight">
            {t('resortDetail.sim.header.title1', "Trova l'appartamento giusto")}
            <span className="text-[var(--gp-gold)]"> {t('resortDetail.sim.header.title2', 'in 4 passi')}</span>
          </h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-2xl">
            <Trans i18nKey="resortDetail.sim.header.description" components={{ b: <b /> }} />
          </p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border shrink-0">
          <Sparkles className="w-3 h-3 mr-1" />
          {computing ? t('resortDetail.sim.badge.recalculating', 'Ricalcolo…') : t('resortDetail.sim.badge.updatedNow', 'Aggiornato adesso')}
        </Badge>
      </div>

      {loadingUnits && !brochureTypes ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('resortDetail.sim.loading.units', 'Caricamento appartamenti…')}
        </div>
      ) : (
        <>
          {/* STEP 1 · Budget ─────────────────────────────────────────── */}
          <StepCard n={1} title={t('resortDetail.sim.step1.title', 'Il tuo budget')} icon={Coins} testid="sim-step-budget">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-2.5">
              {BUDGET_PRESETS.map((b) => {
                const count = properties.filter((p) => p.price >= b.min && p.price <= b.max).length;
                const active = budgetKey === b.key;
                return (
                  <PresetTile
                    key={b.key}
                    active={active}
                    disabled={count === 0}
                    onClick={() => setBudgetKey(b.key)}
                    testId={`sim-budget-${b.key}`}
                    label={t(b.shortKey)}
                    hint={count > 0 ? t('resortDetail.sim.count.units', '{{count}} immobili', { count }) : t('resortDetail.sim.count.none', 'Nessuno')}
                  />
                );
              })}
            </div>
          </StepCard>

          {/* STEP 2 · Metri quadri ────────────────────────────────────── */}
          <StepCard n={2} title={t('resortDetail.sim.step2.title', 'Metri quadri')} icon={Ruler} testid="sim-step-sqm">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-2.5">
              {SQM_PRESETS.map((s) => {
                const count = properties.filter(
                  (p) => p.price >= budget.min && p.price <= budget.max
                      && p.mq   >= s.min      && p.mq   <= s.max,
                ).length;
                const active = sqmKey === s.key;
                return (
                  <PresetTile
                    key={s.key}
                    active={active}
                    disabled={count === 0}
                    onClick={() => setSqmKey(s.key)}
                    testId={`sim-sqm-${s.key}`}
                    label={t(s.shortKey)}
                    hint={count > 0 ? t('resortDetail.sim.count.units', '{{count}} immobili', { count }) : t('resortDetail.sim.count.none', 'Nessuno')}
                  />
                );
              })}
            </div>
          </StepCard>

          {/* STEP 3 · Immobile disponibile ────────────────────────────── */}
          <StepCard n={3} title={t('resortDetail.sim.step3.title', 'Immobile proposto')} icon={Home} testid="sim-step-property">
            {filteredProperties.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-4 text-center">
                <div className="text-sm text-muted-foreground">
                  {t('resortDetail.sim.empty.criteria', 'Nessun immobile combacia con questi criteri. Modifica budget o metri quadri.')}
                </div>
              </div>
            ) : (
              <>
                {/* Highlight immobile selezionato */}
                {currentProp && (
                  <div className="rounded-xl border-2 border-[var(--gp-gold)]/40 bg-white p-3 md:p-4 mb-3 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full grid place-items-center ${currentProp.isAvailable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {currentProp.isAvailable ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="font-serif text-lg text-[hsl(var(--primary))]">
                          {currentProp.label}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {currentProp.mq} mq · <span className={currentProp.isAvailable ? 'text-emerald-700 font-medium' : 'text-slate-500'}>{currentProp.statusLabel}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('resortDetail.sim.property.priceLabel', 'Prezzo')}</div>
                      <div className="font-serif text-xl md:text-2xl text-[var(--gp-gold)] tabular-nums">
                        {fmtEUR(currentProp.price)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Dropdown se >1 opzione */}
                {filteredProperties.length > 1 && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {t('resortDetail.sim.property.changeLabel', 'Cambia immobile ({{total}} corrispondenti · {{available}} disponibili)', { total: filteredProperties.length, available: availableFiltered.length })}
                    </Label>
                    <Select value={selectedId} onValueChange={setSelectedId}>
                      <SelectTrigger className="h-10 mt-1" data-testid="sim-property-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {filteredProperties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <span className="tabular-nums">
                              <b>{p.code}</b> · {p.mq} mq · {fmtEUR(p.price)}
                              {!p.isAvailable && ` · ${t('resortDetail.sim.property.unavailable', 'non disponibile')}`}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </StepCard>

          {/* STEP 4 · Piano rate live ────────────────────────────────── */}
          <StepCard
            n={4}
            title={t('resortDetail.sim.step4.title', 'Piano rate reale · partenza oggi')}
            icon={Calendar}
            testid="sim-step-schedule"
            highlight
            headerRight={
              <Badge variant="outline" className="text-[10px]">
                <RefreshCw className={`w-3 h-3 mr-1 ${computing ? 'animate-spin' : ''}`} />
                {new Date().toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' })}
              </Badge>
            }
          >
            {!quote ? (
              <div className="text-sm text-muted-foreground italic py-6 text-center">
                {computing ? t('resortDetail.sim.schedule.computing', 'Calcolo in corso…') : t('resortDetail.sim.schedule.emptyHint', 'Modifica budget o metri quadri per iniziare.')}
              </div>
            ) : (
              <>
                <div className="space-y-1.5 max-h-[380px] overflow-y-auto pr-1" data-testid="sim-schedule">
                  {quote.schedule.map((s) => <ScheduleRow key={s.step} row={s} dateLocale={dateLocale} />)}
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-[hsl(var(--primary))]/6 px-4 py-3 border border-[hsl(var(--primary))]/12">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-[hsl(var(--primary))]/70 font-semibold">
                    {t('resortDetail.sim.schedule.totalPaid', 'Totale pagato')}
                  </div>
                  <div className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] tabular-nums">
                    {fmtEUR(quote.total)}
                  </div>
                </div>
              </>
            )}
          </StepCard>

          {/* CTA finali */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              className="flex-1"
              disabled={!quote}
              onClick={() => setBookOpen(true)}
              data-testid="sim-cta-book-senior"
            >
              <Video className="w-4 h-4 mr-2" />
              {t('resortDetail.sim.cta.bookSenior', 'Prenota call con un Senior')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 border-[var(--gp-gold)] text-[var(--gp-gold)] hover:bg-[var(--gp-gold)]/10"
              disabled={!quote || pdfLoading}
              onClick={downloadPdf}
              data-testid="sim-cta-download-pdf"
            >
              {pdfLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resortDetail.sim.cta.preparingPdf', 'Preparo il PDF…')}</>
              ) : (
                <><FileDown className="w-4 h-4 mr-2" /> {t('resortDetail.sim.cta.downloadPdf', 'Scarica preventivo PDF')}</>
              )}
            </Button>
          </div>

          {/* Nota legale */}
          <div className="mt-4 flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <div>
              {t('resortDetail.sim.legalNote', "Simulazione indicativa non vincolante. Il piano definitivo viene formalizzato nel contratto preliminare. HCF Property S.r.l. non offre al pubblico quote societarie: il presente strumento riguarda esclusivamente l'acquisto di un bene immobile.")}
            </div>
          </div>
        </>
      )}

      <BookSeniorModal
        open={bookOpen}
        onOpenChange={setBookOpen}
        slug={slug}
        context={{
          subject: `Info simulatore ${slug}`,
          sourceLabel: `simulator_${slug}`,
          unitCode: currentProp?.label || '',
        }}
      />
    </section>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function StepCard({ n, title, icon: Icon, children, testid, highlight, headerRight }) {
  return (
    <Card
      className={`mb-3 ${highlight ? 'border-2 border-[var(--gp-gold)]/50 bg-gradient-to-br from-white to-[var(--gp-gold)]/5' : 'border-border'}`}
      data-testid={testid}
    >
      <CardContent className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full text-white text-xs font-bold grid place-items-center ${highlight ? 'bg-[var(--gp-gold)]' : 'bg-[hsl(var(--primary))]'}`}>
              {n}
            </div>
            <div className="flex items-center gap-1.5 font-medium text-sm md:text-base">
              {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
              {title}
            </div>
          </div>
          {headerRight}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function PresetTile({ active, disabled, onClick, testId, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`rounded-xl border-2 px-2 py-3 text-center transition-all ${
        active
          ? 'border-[var(--gp-gold)] bg-[var(--gp-gold)]/10 shadow-md'
          : disabled
            ? 'border-border/60 bg-muted/30 text-muted-foreground/50 cursor-not-allowed opacity-50'
            : 'border-border hover:border-[var(--gp-gold)]/40 hover:bg-[var(--gp-gold)]/5'
      }`}
    >
      <div className={`font-serif text-base md:text-lg leading-tight ${
        active ? 'text-[var(--gp-gold)]' : 'text-[hsl(var(--primary))]'
      }`}>
        {label}
      </div>
      <div className={`text-[10px] mt-0.5 tabular-nums ${
        active ? 'text-[var(--gp-gold)]/80' : 'text-muted-foreground'
      }`}>
        {hint}
      </div>
    </button>
  );
}

function ScheduleRow({ row, dateLocale = 'it-IT' }) {
  const { t } = useTranslation();
  const fmtDateI18n = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };
  const KIND_META = {
    caparra:  { icon: Sparkles,   bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800' },
    anticipo: { icon: Wallet,     bg: 'bg-[var(--gp-gold)]/8 border-[var(--gp-gold)]/30', text: 'text-[var(--gp-gold)]' },
    rata:     { icon: Calendar,   bg: 'bg-white border-border', text: 'text-[hsl(var(--primary))]' },
    saldo:    { icon: TrendingUp, bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800' },
  };
  const m = KIND_META[row.kind] || KIND_META.rata;
  const Icon = m.icon;

  // Translate the row label based on `kind`, extracting numeric context
  // (percentage / step index) from the backend-provided italian label.
  const translatedLabel = (() => {
    const rawLabel = row.label || '';
    if (row.kind === 'caparra') {
      return t('resortDetail.sim.row.caparra', rawLabel);
    }
    if (row.kind === 'anticipo') {
      const match = rawLabel.match(/(\d+(?:[.,]\d+)?)/);
      const pct = match ? match[1] : '';
      return t('resortDetail.sim.row.anticipo', rawLabel, { pct });
    }
    if (row.kind === 'saldo') {
      const match = rawLabel.match(/(\d+(?:[.,]\d+)?)/);
      const pct = match ? match[1] : '';
      return t('resortDetail.sim.row.saldo', rawLabel, { pct });
    }
    if (row.kind === 'rata') {
      // Try to extract "Rata X di Y" pattern
      const match = rawLabel.match(/(\d+)\s*[a-z]+\s*(\d+)/i);
      if (match) {
        return t('resortDetail.sim.row.rata', rawLabel, { i: match[1], n: match[2] });
      }
      return rawLabel;
    }
    return rawLabel;
  })();

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${m.bg}`}
      data-testid={`sim-row-${row.step}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-8 h-8 rounded-full ${m.text} bg-white/60 border grid place-items-center shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{translatedLabel}</div>
          <div className="text-[11px] text-muted-foreground">
            {fmtDateI18n(row.date)}
          </div>
        </div>
      </div>
      <div className={`text-right shrink-0 tabular-nums font-serif text-base md:text-lg ${m.text}`}>
        {fmtEUR(row.amount)}
      </div>
    </div>
  );
}
